// src/pages/User/Login.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { loginUser } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import SwiftAlert from "../../components/SwiftAlert";
import Logo2 from "../../assets/Logo2.png";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, login } = useAuth();

  // --- STATE ---
  const [isLoading, setIsLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  // --- REDIRECT IF LOGGED IN ---
  useEffect(() => {
    if (!loading && user) {
      navigate("/dashboard", { replace: true });
    }
  }, [loading, user, navigate]);

  useEffect(() => {
    if (location.state?.email) {
      setFormData((prev) => ({ ...prev, email: location.state.email }));
    }
    if (location.state?.successMessage) {
      setSuccessMsg(location.state.successMessage);
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  // --- INPUT HANDLER ---
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formError) setFormError("");
  };

  // --- SUBMIT LOGIC ---
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setFormError("");
    setSuccessMsg("");

    if (!formData.email || !formData.password) {
      setFormError("Please fill in all fields.");
      setIsLoading(false);
      return;
    }

    try {
      const response = await loginUser({
        email: formData.email,
        password: formData.password,
      });

      if (response?.success) {
        if (response.token) {
          localStorage.setItem("jwt_token", response.token);
        }
        login(response.data);
        navigate("/dashboard", { replace: true });
      }
    } catch (err) {
      setFormError(err?.message || "Invalid email or password.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen flex items-center justify-center p-4 font-sans relative overflow-hidden">

      {/* CSS to hide eye icon */}
      <style>{`input::-ms-reveal, input::-ms-clear { display: none; }`}</style>

      {/* Alerts */}
      {formError && <SwiftAlert type="error" message={formError} onClose={() => setFormError("")} />}
      {successMsg && <SwiftAlert type="success" message={successMsg} onClose={() => setSuccessMsg("")} />}

      {/* Background Blobs */}
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-blue-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>
      <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-purple-400/20 rounded-full blur-3xl pointer-events-none md:hidden"></div>

      <div className="bg-white w-full max-w-4xl rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-slate-100 md:min-h-[600px] z-10">

        {/* MOBILE HEADER */}
        <div className="md:hidden bg-blue-600 p-8 pb-12 text-white relative overflow-hidden">
          <div className="absolute top-[-20%] right-[-10%] w-40 h-40 bg-white/10 rounded-full blur-xl"></div>
          <div className="relative z-10">
            <div className="flex items-center gap-2 mb-6 opacity-90">
              <div className="w-11 h-11 bg-white rounded-xl flex items-center justify-center border border-white/10 overflow-hidden">
                <img src={Logo2} alt="ReviewMate logo" className="w-full h-full object-contain rounded-xl scale-150" />
              </div>
              <span className="font-bold tracking-wide text-sm uppercase">ReviewMate</span>
            </div>
            <h2 className="text-2xl font-bold leading-tight">Welcome Back</h2>
            <p className="text-blue-100 text-sm mt-1">Sign in to continue.</p>
          </div>
        </div>

        {/* DESKTOP SIDEBAR */}
        <div className="hidden md:flex md:w-1/2 bg-blue-600 p-12 flex-col justify-between text-white relative overflow-hidden">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-900 opacity-20 rounded-full -ml-10 -mb-10 blur-2xl"></div>

          <div className="relative z-10">
            <Link to="/" className="flex items-center gap-2 mb-8 hover:opacity-80 transition text-sm font-medium">
              <i className="fa-solid fa-arrow-left"></i> Back to Home
            </Link>
            <h2 className="text-3xl font-bold leading-tight mb-4">Welcome Back, Scholar.</h2>
            <p className="text-blue-100 text-sm leading-relaxed opacity-90">
              Resume your learning journey with ReviewMate.
            </p>
          </div>

          {/* REVIEWER ICON SHOWCASE */}
          <div className="my-auto py-8 flex items-center justify-center relative z-10">
            <div className="relative">
              {/* Soft decorative glow */}
              <div className="absolute inset-0 bg-white/10 rounded-3xl blur-2xl transform scale-125"></div>

              {/* Main Reviewer Document Card */}
              <div className="w-32 h-32 bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 flex items-center justify-center transform rotate-6 hover:rotate-0 transition duration-500 shadow-2xl group cursor-default">
                <i className="fa-solid fa-file-contract text-5xl text-white/90 group-hover:scale-110 transition duration-300"></i>
              </div>

              {/* Floating Satellite 1: AI Reviewer Sparkles */}
              <div className="absolute -top-3 -right-4 w-11 h-11 bg-white/15 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center transform -rotate-12 hover:rotate-0 transition duration-300 shadow-lg group cursor-default">
                <i className="fa-solid fa-wand-magic-sparkles text-lg text-blue-200"></i>
              </div>

              {/* Floating Satellite 2: Flashcards & Study Decks */}
              <div className="absolute -bottom-3 -left-4 w-11 h-11 bg-white/15 backdrop-blur-md rounded-xl border border-white/20 flex items-center justify-center transform -rotate-6 hover:rotate-0 transition duration-300 shadow-lg group cursor-default">
                <i className="fa-solid fa-layer-group text-lg text-blue-200"></i>
              </div>
            </div>
          </div>

          <div className="space-y-4 relative z-10">
            <div className="flex items-center gap-3">
              <div className="h-px w-8 bg-blue-400"></div>
              <p className="font-medium text-sm italic opacity-90">"The only way to learn mathematics is to do mathematics."</p>
            </div>
          </div>
        </div>

        {/* RIGHT SIDE: Form */}
        <div className="w-full md:w-1/2 px-6 py-8 md:p-12 flex flex-col justify-center relative">

          <div className="animate-[fadeIn_0.3s_ease-out]">

            {/* Toggle */}
            <div className="flex bg-slate-100 p-1 rounded-xl mb-6">
              <button className="flex-1 py-2 text-sm font-semibold rounded-lg bg-white shadow-sm text-slate-800 cursor-default ring-1 ring-black/5">Log In</button>
              <Link to="/register" className="flex-1 py-2 text-sm font-semibold rounded-lg text-slate-500 hover:text-slate-800 text-center transition hover:bg-white/50">Register</Link>
            </div>

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  placeholder="student@university.edu"
                  className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400"
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-10 rounded-lg border border-slate-200 focus:border-blue-600 focus:ring-2 focus:ring-blue-100 outline-none transition text-sm text-slate-700 placeholder-slate-400"
                    onChange={handleInputChange}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-blue-600 transition p-1 cursor-pointer z-10"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <i className={`fa-solid ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                  </button>
                </div>
                <div className="text-right mt-2">
                  <Link to="/forgot-password" className="text-xs font-semibold text-blue-600 hover:underline">Forgot Password?</Link>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className={`w-full bg-blue-600 text-white py-3 rounded-xl font-bold hover:bg-blue-700 transition shadow-lg shadow-blue-600/20 mt-4 active:scale-[0.98] flex items-center justify-center gap-2 ${isLoading ? "opacity-80 cursor-not-allowed" : ""}`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Signing in...</span>
                  </>
                ) : (
                  "Log In"
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
